import api from "@/api/axios";

const API_URL = "/reflection";

/**
 * Die vorhandenen Vergleichssätze, mit ihrer Größe.
 *
 * Der Satzname war bis dahin ein Freitextfeld ohne Liste — und ein Tippfehler
 * ist von einem Satz ohne Korrekturen nicht zu unterscheiden: beide Male
 * meldet die Diagnose null Zellen. Die Liste trennt die beiden Auskünfte.
 *
 * `n_messages` ist die Zahl der eingefrorenen NACHRICHTEN, nicht der
 * korrigierten Zellen. Die stehen in der Diagnose und nur dort — zwei Stellen,
 * die dieselbe Grundmenge zählen, laufen auseinander.
 */
export const listEvalSets = () => api.get(`${API_URL}/eval-sets`);

/**
 * Einen neuen Vergleichssatz einfrieren.
 *
 * Der Satz nimmt ZUERST alle Nachrichten mit menschlichen Korrekturen auf und
 * füllt danach mit bewerteten ohne Korrektur auf `auffuellen_auf` auf. Die
 * Auffüllung ist nicht Beiwerk: ohne Zellen außerhalb der Korrekturmenge steht
 * `collateral_share` auf `null`, und unter 20 Nachrichten ist `missing_delta`
 * instabil (eine ausgefallene Nachricht ist 1/N).
 *
 * `opts.messageIds` ist die Merkliste. Ist sie gesetzt, TRITT SIE AN DIE STELLE
 * der ersten Charge: der Satz beginnt dann mit genau diesen Nachrichten statt
 * mit allen korrigierten, und aufgefüllt wird wie sonst auch. `min_referenz`
 * zählt dann die Mitglieder — wie viele der gemerkten Nachrichten überhaupt
 * Korrekturen tragen —, nicht mehr den Bestand.
 *
 * 409 heißt entweder "den Satz gibt es schon" — ein eingefrorener Satz wird
 * nie nachgezogen, sonst zeigten alte Gate-Ergebnisse auf eine andere
 * Nachrichtenmenge — oder "zu wenige Nachrichten mit Korrekturen". Beide Texte
 * nennen Zahlen und den nächsten Schritt und werden unverändert gezeigt.
 */
export const createEvalSet = (setName, opts = {}) =>
    api.post(`${API_URL}/eval-sets`, {
        set_name: setName,
        min_referenz: opts.minReferenz ?? 8,
        auffuellen_auf: opts.auffuellenAuf ?? 24,
        quellen: opts.quellen ?? "human",
        message_ids: opts.messageIds ?? [],
    });

/**
 * Die Diagnose: was Menschen an den Modellbewertungen korrigiert haben.
 *
 * Ohne jede Modellbeteiligung — reine Auswertung des Bestands, deshalb
 * jederzeit ladbar und schnell.
 *
 * Die Antwort führt drei Dinge, die NICHT dekorativ sind und in der
 * Oberfläche stehen bleiben müssen:
 *   - `warnung` (null oder ein Satz): solange die Korrekturen im Bestand
 *     skriptgeneriert sind, steht dort genau das. Wer die Cluster ohne diesen
 *     Satz liest, hält eine Attrappe für eine Messung.
 *   - `grenzen`: die Sätze, unter denen die Zahlen gelten (ein Bewerter,
 *     Auswahleffekt, die dreifach überladene 0).
 *   - `n_reference_cells`: unter etwa zehn Zellen sagt sie mehr als jede
 *     Kennzahl darüber — dieselbe Regel wie die Unterpower-Sperre aus R4.
 */
export const getDiagnose = (setName) => {
    const params = {};
    if (setName) params.set_name = setName;
    return api.get(`${API_URL}/diagnose`, { params });
};

/**
 * Der Meta-Prompt, mit dem der Optimierer arbeitet, samt Versionsliste.
 *
 * `active` kann null sein — dann hat noch niemand einen gespeichert, und der
 * Vorschlagslauf hat keine Anweisung, der er folgen könnte.
 */
export const getOptimizerPrompt = () => api.get(`${API_URL}/optimizer-prompt`);

/**
 * Den Meta-Prompt speichern. Legt eine NEUE Version an und aktiviert sie —
 * es gibt kein Überschreiben, jede Fassung bleibt nachvollziehbar stehen.
 *
 * 409 heißt: dieser Text existiert schon als Version. Das ist kein Fehler der
 * Oberfläche, sondern die Auskunft, dass nichts zu speichern war; der Grund
 * steht im Klartext in `detail`.
 */
export const saveOptimizerPrompt = (content, note = "") =>
    api.put(`${API_URL}/optimizer-prompt`, { content, note });

/**
 * Welche Modelle für einen Optimierer-Lauf bereitstehen.
 *
 * Bis zum 18.08. war das Modellfeld ein Freitextfeld mit fest verdrahteter
 * Vorgabe — ein Tippfehler war damit von einem fehlenden Modell nicht zu
 * unterscheiden, denn beide quittiert der Dienst mit demselben Fehler.
 *
 * ACHTUNG, seit dem 19.08. ist diese Liste die EINZIGE Quelle der Auswahl: das
 * Freitextfeld daneben ist gestrichen (Entscheidung Simon). Fällt der Aufruf
 * aus, ist nur noch der bereits eingestellte Name wählbar — die Seite bleibt
 * bedienbar, aber ein anderes Modell ist von hier aus nicht mehr erreichbar.
 *
 * ⚠️ Die Liste kommt über den REFLEXIONS-Pfad und nicht über
 * `/system-settings/llm-models`, obwohl es den Endpunkt gibt und er ebenfalls
 * `/api/tags` abfragt. Dessen `configured_model` ist das `LLM_MODEL` des
 * gui-backends (gemessen `gemma3:4b`, während der Rating-Dienst auf
 * `gemma3:1b` steht) — mit dem Optimierer hat es nichts zu tun. Gefragt werden
 * muss das Ollama, das den Lauf tatsächlich fährt.
 *
 * Je Eintrag: `name`, `eignung` (`optimierer` | `embedding` | `unbekannt`),
 * `denkt`, `context_length` (kann `null` sein — dann sagt Ollama es nicht).
 * `default` ist `OPTIMIZER_MODEL` des async-Backends, `think_global` der dort
 * gesetzte Denkmodus.
 *
 * `frisch` umgeht den Zwischenspeicher des Rating-Dienstes — nötig, nachdem
 * jemand ein Modell nachgezogen hat.
 */
export const getOptimizerModels = (frisch = false) =>
    api.get(`${API_URL}/optimizer-modelle`, frisch ? { params: { frisch: 1 } } : {});

/** Die erzeugten Regelvorschläge, neueste zuerst. */
export const listProposals = () => api.get(`${API_URL}/proposals`);

/**
 * Einen neuen Vorschlag erzeugen lassen.
 *
 * ACHTUNG: der Aufruf ist SYNCHRON und dauert 30 bis 90 Sekunden — er wartet
 * auf den Optimierer-Lauf. Kein 202, kein Polling; die Antwort ist bereits der
 * fertige Vorschlag. Die Oberfläche braucht dafür einen sichtbaren
 * Ladezustand und einen gesperrten Knopf, sonst sieht die Seite aus, als
 * hinge sie, und jemand drückt ein zweites Mal.
 *
 * 409 heißt: der Satz enthält keine Referenzzellen — es gibt nichts, woraus
 * ein Vorschlag entstehen könnte. 502 heißt: der Rating-Service oder Ollama
 * ist nicht erreichbar; das ist ein Verbindungsproblem zwischen den Diensten
 * und kein Anmeldeproblem.
 *
 * `belegbudget` hat DREI Zustände, und `null` ist nicht `0`:
 *   null  — nicht mitgeschickt. Der Lauf verhält sich wie bisher: bis zu drei
 *           Belege aus dem größten Befund, und der Meta-Prompt ist
 *           byte-identisch zu dem vor dieser Änderung.
 *   0     — ausdrücklich keine Belege.
 *   n > 0 — Gesamtbudget über alle Befunde, nach Relevanz verteilt.
 * Deshalb wird das Feld bei `null` gar nicht erst in den Rumpf gelegt: eine
 * mitgeschickte 0 hieße drüben etwas anderes.
 */
export const proposeRule = (setName, model, modus = "append", belegbudget = null) => {
    const rumpf = { set_name: setName, model, modus };
    if (belegbudget !== null && belegbudget !== undefined) {
        rumpf.belegbudget = belegbudget;
    }
    return api.post(`${API_URL}/propose`, rumpf);
};

/**
 * Wie groß der nächste Optimierer-Prompt wird — in Token, vor dem Lauf.
 *
 * ⚠️ Das ist eine MESSUNG, keine Schätzung, und sie kostet entsprechend: der
 * Rating-Dienst fährt dafür einen echten Ollama-Aufruf mit `num_predict: 1`.
 * Warm rund 0,6 s; steht das Modell nicht im Speicher, muss es erst geladen
 * werden (gemessen bis 95 s). Deshalb hängt sie an einem Knopf und läuft
 * nicht bei jedem Tastendruck.
 *
 * Warum überhaupt gemessen: Ollama hat kein `/api/tokenize` (HTTP 404), und
 * eine Zeichenheuristik trägt nicht — an 25 echten Nachrichten schwankt das
 * Verhältnis zwischen 2,80 und 4,76 Zeichen je Token.
 *
 * Die Parameter sind dieselben wie beim Lauf, und das ist der Punkt: gemessen
 * wird der Prompt, den `proposeRule` mit denselben Werten verschicken würde.
 *
 * 409 heißt eines von zwei Dingen, und `detail` sagt welches: der Satz trägt
 * keine Korrekturen · oder das gewählte Modell liegt nicht lokal vor. Der
 * zweite Fall wird ausdrücklich NICHT durch einen Download behoben — ein
 * Modell zu ziehen gehört nicht hinter einen Messknopf.
 */
export const messeMetaPrompt = (setName, model, modus = "append", belegbudget = null) => {
    const rumpf = { set_name: setName, model, modus };
    if (belegbudget !== null && belegbudget !== undefined) {
        rumpf.belegbudget = belegbudget;
    }
    return api.post(`${API_URL}/meta-prompt-messen`, rumpf);
};

/** Einen Vorschlag verwerfen. Er bleibt sichtbar, nur als 'verworfen'. */
export const discardProposal = (proposalId) =>
    api.post(`${API_URL}/proposals/${proposalId}/discard`);

/**
 * Einen Vorschlag als Entwurf übernehmen.
 *
 * Hängt den Regeltext an den aktiven Bewertungsprompt und legt daraus eine
 * neue Prompt-Version im Zustand 'draft' an. Gemessen wird damit noch nichts —
 * das passiert erst im Regressionsvergleich auf /async/prompt-versions.
 *
 * `vorschauHash` ist der Wert, den die Liste zu diesem Vorschlag geliefert hat
 * (Feld `vorschau_hash`) — also die Kennung der Vorschau, die der Mensch beim
 * Klicken vor sich hatte. Er wird nicht hier gerechnet, sondern nur
 * zurückgegeben: das Backend rechnet ihn neu und vergleicht.
 *
 * 409 heißt eines von vier Dingen, und `detail` sagt welches: der Vorschlag ist
 * schon übernommen · derselbe Text existiert bereits als Version (dann nennt
 * `detail` Version und Gate-Ergebnis) · der Aufruf nennt gar keine Vorschau
 * (ein veraltetes Bundle — die Seite neu laden) · die aktive Version hat
 * gewechselt, es entstünde jetzt ein anderer Text als der geprüfte. In allen
 * vier Fällen ist nichts geschrieben worden, und der Satz ist zum Lesen
 * gemacht und wird unverändert gezeigt.
 */
export const adoptProposal = (proposalId, vorschauHash) =>
    api.post(`${API_URL}/proposals/${proposalId}/adopt`, { vorschau_hash: vorschauHash });
